import { LitElement, html, css, nothing } from "lit";
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { UiStateModel, UiStateModelParam } from '../../../src/models/ui-state-model';
import { VesselsModel } from '../../../src/models/vessels-model';
import { ForecastInfo, WeatherModel } from '../../../src/models/weather-model';
import { MigrationChangeEvents, MigrationState, VesselMigrationModel } from '../../../src/models/vessel-migration-model';
import { AuthorizationRoles, AuthService } from '../../../src/services/auth-service';
import { TraceService, EVENT_CATEGORIES } from "../../../src/services/trace-service";
import { AlertId, VesselAlertsModel } from '../../../src/models/vessel-alerts-model';
import { VoyThemeMenu } from './voy-theme-menu';
import { VDRUtils } from '../../../src/utils/vdr-utils';
import { getWeatherFormattedDateTime, prettyTimelapsed } from '../../../src/utils/helpers';
import { VesselPositionModel } from '../../../src/models/vessel-position-model';
import { CoordinatesUtils } from '../../../src/utils/coordinates-utils';
import "@maersk-global/mds-components-core";
import "@maersk-global/mds-components-core/mc-icon";
import '../../voyage-tooltip/src/voyage-tooltip-proxy';
import '../../voy-panel/src/voy-panel';
import '../../release-notes/src/release.notes';
import './voy-theme-menu';
import '../../voyage-tooltip/src/voyage-tooltip';
import "../../voyage-heavy-weather-alarm-limits/src/voyage-heavy-weather-alarm-limits";
import {VoyFleetAlarmModel} from "../../../src/models/voy-fleet-alarm-model";
import {AlarmState} from "../../../src/services/types/fleet-alarms-service.types";
import {AlarmEvents} from "../../../src/models/types/voy-fleet-alarm-model.types";

enum ForecastStatusColors {
   Green = "green",
   Yellow = "yellow",
   Red = "red"
 }

 interface PositionInfo {
  lat: string;
  lon: string;
  timestamp: string;
}

 const isK8s = (<any>window)["appenv"].isK8s;

@customElement('voyage-topbar')
export class VoyageTopbar extends LitElement {
    @property({type: String}) datestr: string = "";
    @property({type: Boolean}) showApplicationLinksDropdown: boolean = false;
    @property({type: Boolean}) showSettingsLinksDropdown: boolean = false;
    @property({type: Boolean}) showReleaseNotes: boolean = false;
    @property({type: Boolean}) showWeatherLimit: boolean = false;
    @property({type: String}) performanceLink: string = "";
    @property({type: String, attribute: 'app-name'}) appName: string = "";
    @property({type: Object}) forecastInfo: ForecastInfo | null = WeatherModel!.latestForecastInfo;
    @property({type: Boolean, attribute: 'show-forecast-info'}) showForecastInfo: boolean = false;
    @property({type: Boolean, attribute: 'include-vessel-settings'}) includeVesselSettings: boolean = false;
    @property({type: Boolean, attribute: 'demo-mode'}) demoMode: boolean = false;
    @property({type: Boolean, attribute: 'demo-mode'}) _positionInfo: PositionInfo | null = null;
    @property({type: String}) expirystr: string = "";
    @property({type: Boolean}) shown: boolean = false;
    @property({type: Boolean}) _isActivityLogOpen: boolean = false;
    @property({type: Boolean}) hasWriteAccess: boolean = AuthService.userHasRole(AuthorizationRoles.WeatherLimitWrite);
    @property({type: Boolean}) hasReadAccess: boolean = AuthService.userHasRole(AuthorizationRoles.WeatherLimitRead);
    @property({type: Boolean}) private _enableActivityLog: boolean = false;
    @property({type: Boolean}) _isActiveAlarm = false;
    @property({type: Object, attribute: false}) private _forecastInfoDiv: HTMLElement | null = null;
    @property({type: String, attribute: false}) private _forecastStatus: string = ForecastStatusColors.Red;
    @property({type: Boolean, attribute: false}) private _showMigrationPanel: boolean = false;
    @property({type: Boolean, attribute: false}) private _migrationState: MigrationState | null = null;
    @property({ type: Boolean }) showLogout = false;
    @property({ type: String }) userName: string = AuthService.getCurrentUserdisplayName();
    private _themeMenu: VoyThemeMenu | null = null;
    private _wakeLock: WakeLockSentinel | null = null;

  constructor() {
    super();

    this._refreshPerformanceLink = this._refreshPerformanceLink.bind(this);
    this._updateWeatherForecastAlert = this._updateWeatherForecastAlert.bind(this);
    this._updateLastPosition = this._updateLastPosition.bind(this);
    this._onMigrateClicked = this._onMigrateClicked.bind(this);
    this._setMigrationState = this._setMigrationState.bind(this);
    this.handleActivityLogClick = this.handleActivityLogClick.bind(this);
    this._onVesselsLoaded = this._onVesselsLoaded.bind(this);
    this._checkActiveAlarms = this._checkActiveAlarms.bind(this);
    this.navigateToHome = this.navigateToHome.bind(this);
    this.handleLogout = this.handleLogout.bind(this);

        const updateDateStr = () => {
            const now = new Date();
            const month = now.toLocaleString('en-us', {month: 'short', timeZone: 'UTC'});
            const timePart = `${
                now.getUTCHours().toString().padStart(2, '0')}:${
                now.getUTCMinutes().toString().padStart(2, '0')}`;

            const datePart = `${now.getUTCDate().toString().padStart(2, '0')} ${month}`
            this.datestr = `UTC: ${datePart} ${timePart}`;
        }

  setInterval(() => {updateDateStr()}, 1000);
  updateDateStr();
}

    disconnectedCallback() {
        UiStateModel!.removeEventListener(`${UiStateModelParam.IMO}-change`, this._refreshPerformanceLink);
        UiStateModel!.removeEventListener('vessels-updated', this._refreshPerformanceLink);
        VesselsModel!.removeEventListener('vessels-loaded', this._onVesselsLoaded);
        WeatherModel!.removeEventListener('weather-updated', this._updateWeatherForecastAlert);
        VesselPositionModel?.removeEventListener('position-updated', this._updateLastPosition);
        VesselMigrationModel?.removeEventListener(MigrationChangeEvents.StateChanged, this._setMigrationState);
        VoyFleetAlarmModel?.removeEventListener(
            AlarmEvents.NotificationSenderAlarmsUpdated,
            this._checkActiveAlarms
        );
    }

    connectedCallback() {
        super.connectedCallback();
        VoyFleetAlarmModel?.addEventListener(
            AlarmEvents.NotificationSenderAlarmsUpdated,
            this._checkActiveAlarms
        );
    }


    static get styles() {
        return [
            css`
      :host {
          display: grid;
          grid-template-columns: auto auto 17fr;
          grid-template-rows: auto;
          align-items: center;
          justify-content: center;
          width: 100%;
          background: var(--neutrals-grey-10, white);
          box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
          user-select: none;
      }
      .name {
          color: var(--primary-blue-900, white);
          font-family: var(--component-heading-ff, 'serif');
          font-size: 20px;
          line-height: 24px;
          font-weight: normal;
          margin: 0 0px 0 24px;
      }
      .logo-divider {
          border-left: 3px solid var(--primary-blue-300, blue);
          width: 1px;
          height: 15px;
          margin: 2px 4px 0px 6px;
      }
      .app-name {
          color: var(--primary-blue-900, blue);
          font-family: var(--component-heading-ff, 'serif');
          font-size: 20px;
          line-height: 24px;
          font-weight: bold;
      }
      .logo {
          grid-column: 1;
          display: flex;
          align-items: center;
          height: 49px;
          cursor: pointer;
      }
      .left {
          grid-column: 3;
          display: flex;
          align-items: center;
          margin-left: 33px;
      }
      .application-links-dropdown {
          position: absolute;
          top: 48px;
          left: 219px;
          min-width: 200px;
          background: var(--neutrals-grey-10, white);
          border: 1px solid var(--neutrals-grey-06, grey);
          box-sizing: border-box;
      }
      .application-info, .application-link {
          display: flex;
          align-items: center;
          color: var(--neutrals-grey-03, black);
          font-family: var(--component-paragraph-ff, 'serif');
          font-size: 18px;
          line-height: 24px;
          font-weight: var(--component-paragraph-weight, normal);
          text-decoration: none;
      }
      .link {
          cursor: pointer;
      }
      .application-link > svg,
      .application-info > svg {
          width: 11px;
          height: 13px;
          fill: currentColor;
      }
      svg {
          margin: 4px;
      }

            .settings {
                display: flex;
                align-items: center;
                cursor: pointer;
                margin-right: 16px;
            }
            .feedback {
                display: flex;
                align-items: center;
                cursor: pointer;
                margin-right: 10px;
            }
            .settings > svg {
                width: 22px;
                height: 22px;
                fill: var(--neutrals-grey-03, black);
            }
            .feedback > svg {
                width: 22px;
                height: 22px;
                fill: var(--neutrals-grey-03, black);
            }
            .application-link {
                padding: 10px 10px 10px 0;
            }
            .version {
                text-transform: uppercase;
                margin-top: -10px;
                border-radius: 6px;
                line-height: 12px;
                padding: 2px 4px 2px 8px;
                font-size: 12px;
                font-family: MaerskText;
            }
            .settings-links-dropdown {
                position: absolute;
                top: 48px;
                right: 15px;
                background: var(--neutrals-grey-10, white);
                border: 1px solid var(--neutrals-grey-07, grey);
                border-radius: 3px;
                box-shadow: 0 2px 4px 0 rgba(0,0,0,0.15);
                box-sizing: border-box;
                padding: 15px;
            }
            .settingsEntry {
                padding: 0;
                display: flex;
                align-items: center;
                color: var(--neutrals-grey-03, black);
                font-family: MaerskText;
                font-size: 14px;
                cursor: pointer;
            }
            .settingsEntry > svg {
                width: 16px;
                height: 16px;
                fill: currentColor;
                margin-left: 0;
            }
            .datetime {
                grid-column: 3;
                color: var(--neutrals-grey-02, black);
                font-family: var(--component-paragraph-ff, 'serif');
                font-size: 16px;
                line-height: 24px;
                font-weight: var(--component-paragraph-weight, normal);
            }
            .expiryDate {
                grid-column: 4;
                margin-left:20px;
                margin-right:auto;
                color: var(--neutrals-grey-02, black);
                font-family: var(--component-paragraph-ff, 'serif');
                font-size: 16px;
                line-height: 24px;
                font-weight: var(--component-paragraph-weight, normal);
                align-items: center;
            }
            .expiry {
                padding-left: 20px;
                font-family: "MaerskText";
                font-size: 14px;
            }

            .right {
                grid-column: 5;
                justify-self: right;
                display: flex;
                align-items: center;
            }
            button {
                background: none;
                border: none;
            }
            .release-notes {
                font-family: MaerskText;
                color: var(--neutrals-grey-03, black);
                padding: 8px 16px;
                display: block;
                position: absolute;
                top: 100px;
                right: 20px;
                width: 600px;
                min-width: 600px;
                background: #f7f7f7;
                border: 1px solid var(--neutrals-grey-06, black);
                border-radius: 6px;
                overflow: auto;
            }
            .release-notes-close {
                position: sticky;
                top: 2px;
                right: 10px;
                text-align: end;
                cursor: pointer;
                fill: var(--neutrals-grey-02, black);
            }
            .release-notes-close > svg {
                width: 10px;
            }
            .weather-limits-overlay {
              position: fixed;
              top: 45px;
              border-top: 1px solid var(--neutrals-grey-06, black);
              width: 100vw;
              height: calc(100vh - 45px);
              display: flex;
              background-color: white;
              z-index: 1000;
              overflow-y: auto;
            }

            .weather-limits-container {
              font-family: MaerskText;
              color: var(--neutrals-grey-03, black);
              padding: 16px;
              background: white;
            }

            .forecast {
                display: flex;
                align-items: center;
                font-family: MaerskText;
                font-size: 12px;
                fill: currentColor;
                border-radius: 3px;
                padding-right: 5px;
            }
            .forecast[status=green] {
                color: var(--voyage-good-green, green);
            }
            .forecast[status=yellow] {
                color: var(--warning-yellow-8, yellow);
            }
            .forecast[status=red] {
                color: var(--voyage-error-red, red);
            }
            .forecastIcon {
                height: 13px;
                width: 15px;
                margin: 5px;
            }
            .forecastIcon>svg {
                height: 100%;
                width: 100%;
                margin: 0;
            }
            .low-res-pill {
                justify-self: flex-end;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 56px;
                height: 18px;
                border-radius: 10px;
                margin-left: 5px;
            }
            .low-res-pill span {
                color: white;
            }
            voyage-tooltip {
                --tooltip-background-color: var(--primary-blue-01);
                font-family: MaerskText;
                font-size: 14px;
            }
            .hard-refresh-text {
                font-size: 14px;
                font-family: MaerskText;
                padding-left: 20px;
                padding-right: 20px;
                color: var(--primary-blue-700, blue);
            }
            .last-position {
                margin-right: 24px;
                font-family: MaerskText;
                font-size: 12px;
                font-weight: 400;
                line-height: 16px;
                letter-spacing: 0px;
                text-align: left;
                color: var(--functional-grey-700, gray);
            }
            .migrate-button {
                margin-left: 10px;
            }
            .migration-panel {
                position: absolute;
                height: 240px;
                width: 423px;
                margin-top: 20px;
            }
            .migration-container {
                display: flex;
                flex-direction: column;
                height: 100%;
                justify-content: space-between;
            }
            .migration-panel-buttons {
                align-self: flex-end;
                position: absolute;
                bottom: 16px;
            }
            .red-dot{    
                width: 12px;     
                height: 12px;     
                background-color: #C83819;     
                border-radius: 50%;    
                position: absolute;     
                top: -1px;     
                left: -1px;
            }
            .failed-message {
					display: flex;
                    box-sizing: border-box;
                    align-items: center;
                    width: 100%;
                    height: 44px;
                    background-color: var(--feedback-danger-light, red);
                    border-radius: 4px;
                    padding: 12px;
                    margin-bottom: 6px;
                    pointer-events: none;
            }
            .failed-message svg {
                width: 16px;
                margin-right: 14px;
            }
            .failed-text {
                padding-bottom: 4px;
            }
            .popup-detail {
                color: var(--neutrals-grey-02, grey);
                font-size: 16px;
                font-family: MaerskText;
                line-height:24px;
                font-weight: 400;
                margin-top: 20px;
                margin-bottom: 10px;
                margin-left: 0px;
            }
            .footer {
                text-align: end;
                padding: 16px 24px;
                margin: -24px;
                margin-top: 20px;
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                align-items: center;
                padding: 16px 24px;
                gap: 12px;
                height: 64px;
                background: #F7F7F7;
                border-bottom-left-radius: 6px;
                border-bottom-right-radius: 6px;
           }
           .activity-log {
                display: flex;
                align-items: center;
                cursor: pointer;
                gap: 5px;
                font-family: MaerskText;
                font-size: 14px;
                margin-right: 16px;
           }
              .activity-log.disabled {
                 cursor: not-allowed;
                 color: var(--neutrals-grey-04, grey);
              }
            .text-success{
                color: #40AB35
            }
            .text-danger{
                color: #A12218;
            }
            .weather-forecast-update{
                margin-right: 16px;
                font-family: Maersk Text;
                font-size: 13px;
                font-weight: 400;
                line-height: 24px;
            }

            .logout {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 35px;
                height: 35px;
                background-color: rgb(255, 192, 203, 0.5); 
                border-radius: 50%;
                cursor: pointer;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                margin-right: 16px;
            }

            .logout:hover {
                background-color: rgb(255, 192, 203, 0.75); 
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
            }

            .user-initials {
                font-size: 14px;
                font-weight: bold;
                color: #333;
                text-transform: uppercase;
            }

            .logout-panel {
                position: absolute;
                top: 50px;
                right: 0;
                background-color: white;
                border: 1px solid #ddd;
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
                border-radius: 5px;
                z-index: 100;
                padding: 8px;
                min-width: 120px;
            }

            .logout-row button {
                display: flex;
                align-items: center;
                padding: 8px 12px;
                font-size: 15px;
                border: 1px solid #ddd;
                border-radius: 5px;
                padding: 5px 11px;
                cursor: pointer;
                transition: background-color 0.3s ease;
            }

            .logout-row {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 5rem;
                padding: 5px;
            }

            .logout-row p {
                margin: 0;
                font-size: 16px;
                color: #333;
            }

            .logout-row button svg {
                margin-right: 6px;
                width: 16px;
                height: 16px;
            }
        `];
    }

    handleFeedbackButtonClick() {
      const feedbackEvent = new CustomEvent('show-feedback', {
          bubbles: true,
          composed: true
      });
      this.dispatchEvent(feedbackEvent);
      this.showSettingsLinksDropdown = false;
  }

    handleLogout(){
        return html`
            <div class="logout-panel">
                <div class="logout-row">
                    <p>${this.userName}</p>
                    <button @click=${() => this.handleLogOutClick()}>${SvgIcons.arrowFromLeft}Log out</button>
                </div>
            </div>
        `
    }

    handleLogOutClick(){
        localStorage.clear();
        sessionStorage.clear();
        AuthService.logOut();
    }

    private getUserInitials(name: string): string {
        if (!name) return "";
        const splitName = name.trim().split(" ");
        const firstInitial = splitName[0]?.charAt(0).toUpperCase() || "";
        const lastInitial = splitName.length > 1 ? splitName[splitName.length - 1]?.charAt(0).toUpperCase() : "";
        return `${firstInitial}${lastInitial}`;
    }
    
    private toggleLogoutButton() {
        this.showLogout = !this.showLogout;
    }

    private _checkActiveAlarms() {
        const alarms = VoyFleetAlarmModel?.getWeatherAlarmsForNotificationSender;
        this._isActiveAlarm = alarms?.some(alarm => alarm.alarmState === AlarmState.Active) ?? false;
    }
///
    handleActivityLogClick() {
        const activityLogEvent = new CustomEvent('show-activity-log', {
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(activityLogEvent);
        this.showSettingsLinksDropdown = false;
    }

    navigateToHome(){
        Object.values(UiStateModelParam).map(param => {
            if(param !== UiStateModelParam.SelecteMapView){
                UiStateModel!.setValue(param);
            }
        })
    }

  render() {
    return html`
    <div class="logo" @click=${() => this.navigateToHome()}>
        <span class="name">StarConnect</span>
        <div class="logo-divider"></div>
        <span class="label app-name">${this.appName}</span>
        <div class="application-info ${VDRUtils.isVDRVessel() ? "" : "link"}" @click=${this._applicationInfoClicked}>
            ${VDRUtils.isVDRVessel() ? html`` : html`
            <span class="version">${(<any>window)["appenv"].appVersion}</span>
            `}
        </div>
    </div>
    <div class="left">
        ${this.showApplicationLinksDropdown ? html`
            <div class="application-links-dropdown">
                <a class="application-link" target="_blank" href=${this.performanceLink}>
                    ${SvgIcons.lightTachometerSlow}
                    <span class="label">Performance</span>
                </a>
            </div>` : html``}
            ${this.appName !== 'Fleet Voyage' ? html`
            <div>
                ${!isK8s && this._migrationState !== MigrationState.MigrationServiceNotFound ? html`
                    <mc-button
                        class="migrate-button"
                        label="Migrate"
                        variant="outlined"
                        fit="small"
                        @click=${this._onMigrateClicked}
                    >
                    </mc-button>`
                    : nothing}
               
                ${this._showMigrationPanel ?
                    html`
                        <voy-panel class="migration-panel" panelTitle="Migrate">
                            ${this._migrationElement()}
                        </voy-panel>
                    `
                    : nothing
                }
            </div>` : nothing
            }
            <div class="datetime">${this.datestr}</div>
            </span>
      </div>
        <div class="right">
            ${this.showForecastInfo ? html`
            <div>
                <p class="last-position">Position: ${this._positionInfo?.timestamp}</p>
                <voyage-tooltip-proxy
                    text=${`Last position update <br> ${this._positionInfo?.lat} <br> ${this._positionInfo?.lon}`}
                    html-content
                    .positionPriority=${["bottom"]}
                    .tooltipInteraction=${true}
                    >
                </voyage-tooltip-proxy>
              </div>
            <div class="forecast" status=${this._forecastStatus}> Weather:
                    ${this._getForecastVersionText()}
                    ${!this.forecastInfo?.hasHighResData ? html`<div class="low-res-pill"><span>Low Res</span></div>` : html``}
                </div>
            `: html`` }
            <div class="left">
            <div class="weather-forecast-update ${this.getWeatherClass()}">Weather: ${getWeatherFormattedDateTime(new Date(WeatherModel?.latestForecastInfo?.timestamp || ''))}</div>
                <div class="activity-log ${!this._enableActivityLog ? 'disabled' : ''}"
                     @click=${this._enableActivityLog ? this.handleActivityLogClick : null}>
                    <div style="position: relative">
                        <mc-icon size="24" color=${!this._enableActivityLog ? 'var(--neutrals-grey-04, grey)' : ''}
                                 icon="file">
                        </mc-icon>
                        <!-- Show the red dot when isActiveAlarm is true -->
                        ${this._isActiveAlarm ? html`
                                <span class="red-dot"></span>
                            ` : ''}
                    </div>
                    Activity Log
                </div>
            <div class="feedback" @click=${this.handleFeedbackButtonClick}>
                    ${SvgIcons.feedbackIcon}
                </div>
                <div class="settings" @click=${() => {this.showSettingsLinksDropdown = !this.showSettingsLinksDropdown;}}>
                    ${SvgIcons.lightCog}
                </div>
                ${this.showSettingsLinksDropdown ? this._renderSettingsMenu() : html``}
                <div class="logout" @click=${this.toggleLogoutButton}>
                    <div class="user-initials">
                            ${this.getUserInitials(this.userName)}
                    </div>
                </div>
                ${this.showLogout? this.handleLogout() : html``}           
            </div>
      </div>
      ${this.showReleaseNotes ? html`
            <span class="release-notes">
              <div class="release-notes-close" @click=${() => this.showReleaseNotes = !this.showReleaseNotes}>${SvgIcons.lightTimes}</div>
              <release-notes></release-notes>
            </span>
          `
        : ""}
      ${this.showWeatherLimit && (this.hasReadAccess || this.hasWriteAccess)
        ? html` <div class="weather-limits-overlay">
            <div class="weather-limits-container">
              <voyage-heavy-weather-alarm-limits
                @cancel=${() => (this.showWeatherLimit = false)}
              ></voyage-heavy-weather-alarm-limits>
            </div>
          </div>`
        : ""}
      ${ this.showForecastInfo ? html`
        <voyage-tooltip position-priority='["bottom"]' .for=${this._forecastInfoDiv}>${this._forecastVersionTooltipText()}</voyage-tooltip>
        `: html`` }
    `;
  }

  firstUpdated() {
    this._forecastInfoDiv = this.shadowRoot!.querySelector(".forecast");
    UiStateModel!.addEventListener(`${UiStateModelParam.IMO}-change`, this._refreshPerformanceLink);
    VesselsModel!.addEventListener("open-heavy-weather-comapny-limits",()=>this.showWeatherLimit = true);
    UiStateModel!.addEventListener('vessels-updated', this._refreshPerformanceLink);
    VesselsModel!.addEventListener('vessels-loaded', this._onVesselsLoaded);
    WeatherModel!.addEventListener('weather-updated', this._updateWeatherForecastAlert);
    VesselPositionModel?.addEventListener('position-updated', this._updateLastPosition);
    VesselMigrationModel?.addEventListener(MigrationChangeEvents.StateChanged, this._setMigrationState);

    this._updateWeatherForecastAlert();
    this._refreshPerformanceLink();
    if(this.appName !== "Fleet Voyage") {
        this._setMigrationState();
    }
}

    private _setMigrationState() {
        this._migrationState = VesselMigrationModel!.migrationState;
    }

private _migrationElement() {
  if (this._migrationState === MigrationState.Windows) {
      return this._readyToMigrateElement();
  }

  if (this._migrationState === MigrationState.MigrationFailed) {
    return this._failedMigrateElement();
}

    return nothing;
  }

  private _readyToMigrateElement() {
    return html`
        <div class="migration-container">
            <div>
                Please accept to migrate to the new platform, it will not take longer than 15 minutes.
            </div>
            <div class="migration-panel-buttons">
                <mc-button
                    label="Do it later"
                    variant="outlined"
                    @click=${() => {this._showMigrationPanel = false}}
                >
                </mc-button>
                <mc-button
                    label="Accept"
                    @click=${() => VesselMigrationModel!.startMigration()}
                >
                </mc-button>
            </div>
        </div>
    `
}

private _failedMigrateElement() {
  return html`
      <div class="migration-container">
          <div>
              <div class="failed-message">
                  <span>${SvgIcons.lightExclamationCircle}</span>
                  <span class="failed-text">An error has occured.</span>
              </div>
              <div>
                  Support will solve the issue and you will be contacted as soon as possible. Thank you for your patience.
              </div>
          </div>
      </div>
  `
}

private _onMigrateClicked() {
  this._showMigrationPanel = !this._showMigrationPanel;
}

private _renderSettingsMenu() {
  return html`
  <div class="settings-links-dropdown">
      <button class="settingsEntry"
              @click=${() => { this.showReleaseNotes = true;
                               this.showSettingsLinksDropdown = false; }}>
          ${SvgIcons.clipboardList} Release Notes
      </button>
      ${this.includeVesselSettings ? html`
      <button class="settingsEntry"
              @click=${() => { this.dispatchEvent(new Event("show-weather-limits"));
                               this.showSettingsLinksDropdown = false; }}>
          ${SvgIcons.sunCloud} Weather Limits
            </button>
            <!-- to be enabled in a later release
	    <button class="settingsEntry"
                    @click=${() => { this.dispatchEvent(new Event("show-restriction-areas"));
                                     this.showSettingsLinksDropdown = false; }}>
            }}>
                ${SvgIcons.ban} Restriction Areas
            </button> -->
          `
        : html``}
            ${this.includeVesselSettings ? html`
            <button class="settingsEntry"
                    @click=${() => { this.dispatchEvent(new Event("show-vessel-characteristics"));
                                     this.showSettingsLinksDropdown = false; }}>
                ${SvgIcons.ship} Vessel Characteristics
            </button>
             <button class="settingsEntry"
                    @click=${() => { this.dispatchEvent(new Event("show-vessel-position"));
                                     this.showSettingsLinksDropdown = false; }}>
                ${SvgIcons.mapMarked} Vessel Position
            </button>
            <button class="settingsEntry" id="themeButton"
                    @click=${() => { this._themeClicked() }}>
                ${SvgIcons.paintBrush} Change Theme
            </button>
            ` : html``}
            ${!this.includeVesselSettings ? html`
            <button class="settingsEntry" id="themeButton"
                    @click=${() => { this._toggleDemoMode() }}>
                ${SvgIcons.desktop} ${this.demoMode ? 'Stop' : 'Start'} Demo Mode
            </button>

            ${this.hasReadAccess || this.hasWriteAccess ? html `<button
              class="settingsEntry"
              @click=${() => {
                this.showWeatherLimit = true;
                this.showSettingsLinksDropdown = false;
              }}
            >
              ${SvgIcons.sunCloud} Heavy Weather Alarm Limits

            </button>` : html ``}
          `
        : html``}
    </div>`;
  }

  private _tracingAnalyticsApplicationChange() {
    TraceService!.event(EVENT_CATEGORIES.TOPBAR, 'click', 'Application Changed');
  }

  private _updateWeatherForecastAlert() {
    this.forecastInfo = WeatherModel!.latestForecastInfo;
    if (this.showForecastInfo) {
        this._updateForecastStatus();
        if (this._forecastStatus === ForecastStatusColors.Red) {
            VesselAlertsModel!.addAlert((<any>window)["appenv"].vesselIMONumber, {
                message: "Current Weather forecast is outdated.",
                id: AlertId.OutdatedWeather
            });
        } else {
            VesselAlertsModel!.removeAlert((<any>window)["appenv"].vesselIMONumber, AlertId.OutdatedWeather);
        }
    }
  }

  private _onVesselsLoaded() {
    this._enableActivityLog = true;
  }

  private _refreshPerformanceLink() {
    this.performanceLink = `${(<any>window)["appenv"].services.scfrontend.baseurl}/`;

    // If there is a vessel selected that supports the StarConnect performance pages, deep-link to it
    const imo = UiStateModel!.getValue('imo');
    if (imo) {
        const vessel = VesselsModel!.getVessel(parseInt(imo, 10));
        if (vessel && vessel.hasPAPI) {
            this.performanceLink = `${(<any>window)["appenv"].services.scfrontend.baseurl}/vessel/${imo}/overview`;
        }
    }
  }

  private _updateLastPosition() {
    const positionObj = VesselPositionModel!.position;
    const coords = positionObj
     && positionObj.longitude
     && positionObj.latitude
     && CoordinatesUtils.decimalToDegreesAndMinutes([positionObj.longitude, positionObj.latitude ]);
     if (coords && positionObj?.timestamp) {
         this._positionInfo = {
             lat: coords.lat,
             lon: coords.lon,
             timestamp: prettyTimelapsed(positionObj.timestamp)
         }
     }
  }

  private _getForecastVersionText() {
    if (!this.forecastInfo) {
        return "No forecast available!";
    } else {
        const forecastDate = new Date(this.forecastInfo.timestamp);
        const day = forecastDate.getUTCDate().toString().padStart(2,'0');
        const month = forecastDate.getUTCMonth() + 1 < 10 ? `0${forecastDate.getUTCMonth() + 1}` : forecastDate.getUTCMonth() + 1;
        const year = forecastDate.getUTCFullYear();
        const hours = forecastDate.getUTCHours().toString().padStart(2,'0');
        const minutes = forecastDate.getUTCMinutes().toString().padStart(2,'0');

        const ageInHours = (Date.now() - new Date(this.forecastInfo.timestamp).getTime())/(60*60*1000);

        return `+${Math.floor(ageInHours)} (UTC ${hours}:${minutes} - ${day}/${month} )`;
    }
  }

  private _forecastVersionTooltipText() {
    const msg = "Last weather update";

    if (!this.forecastInfo?.hasHighResData) {
        return html`${msg} 
            <br>Resolution is low (2.5 degrees)
            <br>because of poor network connectivity.`
    }
    return msg;
  }

  private _updateForecastStatus() {
    if (!this.forecastInfo)
    {
        this._forecastStatus = ForecastStatusColors.Red;
        return
    }

    const forecastAge = Date.now() - new Date(this.forecastInfo.timestamp).getTime();

    if (forecastAge < 12*60*60*1000)
      {
          this._forecastStatus = ForecastStatusColors.Green;
      }
      else if (forecastAge < 24*60*60*1000)
      {
          this._forecastStatus = ForecastStatusColors.Yellow;
      }
      else
      {
          this._forecastStatus = ForecastStatusColors.Red;
      }
  }

  private _applicationInfoClicked()
  {
      if (!VDRUtils.isVDRVessel())
      {
          this.showApplicationLinksDropdown = !this.showApplicationLinksDropdown;
          this._tracingAnalyticsApplicationChange();
      }
  }

  private async _themeClicked()
  {
      if (!this._themeMenu)
      {
          this._themeMenu = <VoyThemeMenu>document.createElement('voy-theme-menu');

          // Position menu relative to the theme button
          const themeButton = this.shadowRoot!.getElementById("themeButton");
          const rect = themeButton!.getBoundingClientRect();
          this._themeMenu.top = rect.top;
          this._themeMenu.right = window.innerWidth - rect.left + 10;

          this._themeMenu.addEventListener('close', () => {
            this.showSettingsLinksDropdown = false;
            this._themeMenu = null;
        }, { once: true } );

        document.body.appendChild(this._themeMenu);
    }
  }

  private async _toggleDemoMode() {
    this.demoMode = !this.demoMode;

    if (this.demoMode) {
        // Go fullscreen
        if (!document.fullscreenElement) {
            document.body.requestFullscreen().catch(err => { console.log(`Error attempting to go full-screen ${err.message}`)});
        }

        // Attempt to do Screen Wake Lock
        if ('wakeLock' in navigator) {
            try {
                this._wakeLock = await navigator.wakeLock.request('screen');
                this._wakeLock.addEventListener('release', () => {
                    console.log("Released Wake Lock");
                    this._wakeLock = null;
                });

                console.log("Obtained Wake Lock");
            } catch (err) {
                console.error(`${(<Error>err).name}, ${(<Error>err).message}`);
            }
        }

        UiStateModel!.setValue("demo", "on");
    } else {
        // Disable fullscreen
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }

        // Release Screen Wake Lock
        if (this._wakeLock) {
            this._wakeLock.release();
        }

        UiStateModel!.setValue("demo");
    }
}

private _getUTCDate(dateVal: any) {
  const utcYear = dateVal.getUTCFullYear();
  const utcMonth = dateVal.getUTCMonth() + 1; // Month is zero-indexed
  const utcDay = dateVal.getUTCDate();
  const utcHours = dateVal.getUTCHours();
  const utcMinutes = dateVal.getUTCMinutes();
  const utcSeconds = dateVal.getUTCSeconds();
  return Date.UTC(utcYear, utcMonth, utcDay, utcHours, utcMinutes, utcSeconds)
}

getWeatherClass(){
    let forecastDate = new Date(WeatherModel?.latestForecastInfo?.timestamp || '');
    let dateNow = new Date();
    let weatherUpdatedHours = Math.round(Math.abs(forecastDate.getTime() - dateNow.getTime())/(60*60*1000));
    return weatherUpdatedHours >= 24 ? 'text-danger' : 'text-success';
}
}